import { useState, useEffect } from 'react';
import { analyticsFilterService } from '../services/analyticsService';
import type { FilterOption } from '../types/analytics';

interface CycleOption {
    value: string;
    label: string;
}

interface UseCycleOptionsResult {
    options: CycleOption[];
    loading: boolean;
    error: string | null;
}

/**
 * Fetches cycle options from the analytics filter API for use in dropdowns.
 * @param filterKey - 'review_cycle_id' for review cycles, 'goal_cycle_id' for goal cycles
 */
export const useCycleOptions = (filterKey: 'review_cycle_id' | 'standard_review_cycle_id' | 'goal_cycle_id' | 'grid_cycle_id'): UseCycleOptionsResult => {
    const [options, setOptions] = useState<CycleOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchOptions = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await analyticsFilterService.getFilterOptions(filterKey);
                const cycleOptions: CycleOption[] = (response.options || []).map((opt: FilterOption) => ({
                    value: opt.value,
                    label: opt.label,
                }));
                setOptions(cycleOptions);
            } catch (err: any) {
                console.error(`Failed to fetch ${filterKey} options:`, err);
                setError(err.message || 'Failed to load cycle options');
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [filterKey]);

    return { options, loading, error };
};
